<template>
  <div>
    <b-modal
      id="dayManagerOptions"
      modal-class="modal-cover"
      v-model="isVisible"
      hide-backdrop
      hide-footer
      hide-header
      content-class="shadow-lg day-manager-content"
      @hidden="onModalHidden"
    >
      <!-- Own header rather than b-modal's. Bootstrap's close button is
           pushed over with `margin-left: auto` inside a header that stays
           LTR whatever the page language, so in Hebrew it landed on the
           right, opposite the side a reader reaches for, and the title sat
           below it in the body leaving an empty bar above. -->
      <section class="day-manager" :dir="isHebrew ? 'rtl' : 'ltr'">
        <header class="day-manager-header">
          <h3 class="day-manager-title">
            {{ t('dayManagerTitle') }} {{ formattedDate }}
          </h3>
          <button
            type="button"
            class="day-manager-close"
            :aria-label="t('close')"
            @click="isVisible = false"
          >
            <span class="material-icons" aria-hidden="true">close</span>
          </button>
        </header>

        <div class="day-manager-actions">
          <b-button
            v-if="isValidDayIndex"
            variant="primary"
            class="action-button"
            @click="addAttraction"
          >
            <i class="fas fa-plus"></i> {{ t('dayManagerAddAttraction') }}
          </b-button>

          <b-button
            v-if="isValidDayIndex && !hasBookedAccommodation"
            variant="success"
            class="action-button"
            @click="addAccommodation"
          >
            <i class="fas fa-bed"></i> {{ t('dayManagerAddAccommodation') }}
          </b-button>

          <b-button
            v-if="isValidDayIndex"
            variant="primary"
            class="action-button"
            @click="addDayAfter"
          >
            <i class="fas fa-plus"></i> {{ t('dayManagerAddDayAfter') }}
          </b-button>

          <!-- Scheduling a WhatsApp message for this day. Admin-only
               while the feature rolls out (every message costs a
               template send per traveller), hence the extra gate the
               other buttons here do not need. -->
          <b-button
            v-if="isValidDayIndex && canScheduleOrganizerMessage"
            variant="light"
            class="action-button whatsapp-schedule-button"
            @click="scheduleOrganizerMessage"
          >
            <WhatsAppBadgeIcon :size="26" />
            {{ t('organizerMessageAddButton') }}
          </b-button>

          <b-button
            v-if="isValidDayIndex"
            variant="danger"
            class="action-button"
            @click="deleteDay"
          >
            <i class="fas fa-trash-alt"></i> {{ t('dayManagerDeleteDay') }}
          </b-button>
        </div>
      </section>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import moment from 'moment';
import { translationMixin } from '@/mixins/translationMixin';
import { canManageOrganizerMessagesFor } from '@/mixins/organizerMessagesMixin';
import WhatsAppBadgeIcon from '@/components/WhatsAppBadgeIcon.vue';

export default {
  props: ['value'],
  mixins: [translationMixin],
  components: { WhatsAppBadgeIcon },
  
  computed: {
    ...mapGetters(['allRide', 'rideHeader', 'inerModalParameters', 'compareToUserId', 'userId']),

    // Same gate the calendar's own organizer UI uses: a private ride you manage.
    canScheduleOrganizerMessage() {
      return canManageOrganizerMessagesFor(this.allRide?.ride, this.compareToUserId);
    },
    
    isVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    
    isValidDayIndex() {
      return this.inerModalParameters?.dayIndex !== undefined && 
             this.allRide?.attractionsByDay && 
             this.inerModalParameters.dayIndex < this.allRide.attractionsByDay.length;
    },
    
    formattedDate() {
      if (!this.inerModalParameters?.date) {
        return 'Loading...';
      }
      try {
        return moment(this.inerModalParameters.date).format('DD/MM/YYYY');
      } catch (error) {
        console.error('Error formatting date:', error);
        return 'Invalid Date';
      }
    },
    
    hasBookedAccommodation() {
      if (!this.isValidDayIndex) return false;
      
      const dayActivities = this.allRide.attractionsByDay[this.inerModalParameters.dayIndex];
      return dayActivities && this.booked(dayActivities);
    }
  },
  
  methods: { 
    ...mapActions([
      'showModal',
      'showModalWithParameters',
      'addNewDayToRide',
      'goToNewAttraction',
      'serchAttractionToRide',
      'goToBaseRideAccommodation',
      'requestOrganizerMessageForDay'
    ]),

    // Hands the date to TripCalendarView, which owns OrganizerMessageModal.
    // See organizerMessageDayRequest in store/modules/popups.js for why this
    // goes through the store instead of a $ref.
    //
    // The date is derived from the trip's start_date + dayIndex, the same
    // way dateForDayIndex does it, rather than from inerModalParameters.date
    // -- that one is FullCalendar's own Date for the cell, and formatting it
    // back to a day depends on the browser's timezone. Every other part of
    // this feature keys off the trip's derivation, and the two must agree or
    // a message is scheduled against the wrong day.
    scheduleOrganizerMessage() {
      if (!this.isValidDayIndex || !this.canScheduleOrganizerMessage) return;

      const start = this.allRide?.ride?.start_date;
      if (!start) return;

      this.requestOrganizerMessageForDay(
        moment(start, 'DD-MM-YYYY')
          .add(this.inerModalParameters.dayIndex, 'days')
          .format('YYYY-MM-DD'));
      this.isVisible = false;
    },
    
    booked(dailyActivities) {
      return dailyActivities?.accommodation?.status === 'booked';
    },

    addAccommodation() {
      if (!this.isValidDayIndex) return;
      
      const dailyActivities = this.allRide.attractionsByDay[this.inerModalParameters.dayIndex];
      if (dailyActivities?.base_trip_accommodation) {
        this.goToBaseRideAccommodation({
          baseRideAccommodation: dailyActivities.base_trip_accommodation,
          dayIndex: this.inerModalParameters.dayIndex
        });
      }
      this.isVisible = false;
    },

    addAttraction() {
      if (!this.isValidDayIndex) return;
      
      if (this.allRide?.ride?.is_based_ride && this.compareToUserId(this.allRide.ride.authorId)) {
        this.goToNewAttraction(this.inerModalParameters.dayIndex);
      } else {
        this.serchAttractionToRide(this.inerModalParameters.dayIndex);
      }
      this.isVisible = false;
    },

    async addDayAfter() {
      if (!this.isValidDayIndex) return;
      var index = this.inerModalParameters.dayIndex;
      this.showModal("loadingIcon");
      const success = await this.addNewDayToRide( { dayIndex: index } );
      
      // Only hide the modal if the operation was successful
      // If there was an error, keep the modal open so the user can see the error message
      if (success) {
        this.showModal("");
      }
      // at the else we are printing the error at the addNewDayToRide
      this.isVisible = false;
    },

    deleteDay() {
      if (!this.isValidDayIndex) return;
      
      // Check if this day has extra information
      const dayDate = this.allRide.attractionsByDay[this.inerModalParameters.dayIndex]?.dateTime;
      const hasExtraInfo = dayDate && this.rideHeader?.ride?.rideExtraInformationByDay?.find(
        info => info.date === dayDate
      );
      
      if (hasExtraInfo) {
        // Show warning about losing extra information first
        this.showModalWithParameters({
          type: 'confirmRemoveDayWithExtraInfo',
          inerModalParameters: {
            dayIndex: this.inerModalParameters.dayIndex
          }
        });
      } else {
        // No extra info, go directly to regular removal confirmation
        this.showModalWithParameters({
          type: 'areYouShureRemovingDay',
          inerModalParameters: {
            dayIndex: this.inerModalParameters.dayIndex
          }
        });
      }
      this.isVisible = false;
    },

    onModalHidden() {
      // this is an solution to solve an issue that the scrall is jumping after closing the date options
      // Prevent focus restoration that causes unwanted scrolling
      // Save current scroll position
      const currentScrollY = window.scrollY;
      const currentScrollX = window.scrollX;
      
      // Use setTimeout to restore scroll position after any focus restoration attempts
      setTimeout(() => {
        window.scrollTo(currentScrollX, currentScrollY);
      }, 0);
    }
  }
};
</script>

<style scoped>
/* px, never rem, throughout: this app sets html { font-size: 10px }, so the
   Bootstrap-ish rem sizes this modal used resolved tiny -- the action
   buttons' 1.1rem was 11px and the title's 1.5rem was 15px, which the owner
   found too small to read (2026-10-05). */
.day-manager {
  padding: 2px;
}

.day-manager-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  margin-bottom: 18px;
}

.day-manager-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
  color: #15312E;
  text-align: center;
  /* Clear of the close button on EITHER side, so the centred title never
     collides with it when the language (and so the button's side) flips. */
  padding: 0 46px;
}

.day-manager-close {
  position: absolute;
  /* inset-inline-END, not start: a close button sits at top-RIGHT in an
     LTR layout, so it mirrors to top-LEFT in Hebrew. `start` would put it
     back on the right in RTL, which is exactly where Bootstrap's always-LTR
     header was leaving it. */
  inset-inline-end: 0;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* 44px: a real touch target, this being tapped on a phone. */
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #5A6B68;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.day-manager-close:hover,
.day-manager-close:focus {
  background: #F0F3F2;
  color: #15312E;
}

.day-manager-close .material-icons {
  font-size: 26px;
}

.day-manager-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  width: 100%;
  /* >=44px: tapped on a phone. */
  min-height: 50px;
  padding: 12px 16px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 10px;
}

/* Owner's pick, 2026-10-05 (option C of six): the logo carries its own
   brand-green disc (WhatsAppBadgeIcon) and the button itself stays quiet.
   The four buttons around this one are already teal, green and red, so a
   fifth coloured button both buried the logo and competed for attention --
   the near-neutral ground is what lets the mark read and keeps the menu
   calm. Two classes so these beat Bootstrap's .btn-light without
   !important. */
.action-button.whatsapp-schedule-button {
  background-color: #ffffff;
  border: 1px solid #DDE3E2;
  color: #0B3B36;
}

.action-button.whatsapp-schedule-button:hover,
.action-button.whatsapp-schedule-button:focus,
.action-button.whatsapp-schedule-button:active {
  background-color: #F4F8F7;
  border-color: #25D366;
  color: #0B3B36;
}

:deep(.day-manager-content) {
  border-radius: 16px;
  border: 0;
}

:deep(.day-manager-content .modal-body) {
  padding: 20px;
}
</style> 